import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/vacancies", label: "Vacancies" },
  { to: "/insights", label: "Insights" },
  { to: "/submit-cv", label: "Submit CV" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // pages where the hero is a dark image and the navbar overlays it
  const isHeroPage = location.pathname === "/" || location.pathname === "/services";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const overHero = isHeroPage && !scrolled;
  const textColor = overHero ? "text-white" : "text-[#0E1530]";
  const hoverColor = overHero ? "hover:text-[#2EA3F2]" : "hover:text-[#1E2A78]";
  const activeColor = overHero ? "text-[#2EA3F2]" : "text-[#1E2A78]";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0E1530]/92 backdrop-blur-xl border-b border-white/10"
          : isHeroPage
          ? "bg-transparent"
          : "bg-[#F4F3F0]/85 backdrop-blur-xl border-b border-[#E2E0D9]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between h-24">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src="/brand/b7-logo-transparent.png"
            alt="B7 Search & Selection"
            className={`h-16 w-auto transition-all duration-500 ${
              scrolled || overHero ? "brightness-0 invert" : ""
            }`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                  isActive
                    ? (scrolled ? "text-[#2EA3F2]" : activeColor)
                    : `${scrolled ? "text-white/85" : textColor} ${scrolled ? "hover:text-[#2EA3F2]" : hoverColor}`
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className={`inline-flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold transition-all ${
              scrolled
                ? "bg-[#2EA3F2] text-[#0E1530] hover:bg-white"
                : overHero
                ? "bg-white text-[#0E1530] hover:bg-[#2EA3F2]"
                : "bg-[#1E2A78] text-white hover:bg-[#162058]"
            }`}
          >
            Get in touch
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`lg:hidden p-2 ${scrolled ? "text-white" : overHero ? "text-white" : "text-[#0E1530]"}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0E1530] border-t border-white/10" data-testid="nav-mobile-menu">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.2em] uppercase font-semibold ${
                    isActive ? "text-[#2EA3F2]" : "text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-2 px-6 py-3 bg-[#2EA3F2] text-[#0E1530] text-[11px] tracking-[0.22em] uppercase font-semibold"
              data-testid="nav-mobile-cta"
            >
              Get in touch
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
